import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "./ui/carousel";
import { ArrowLeft, ArrowRight } from "lucide-react";
import WheelGesturesPlugin from "embla-carousel-wheel-gestures";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";

interface AdvancedCarouselProps {
  title: string;
  imageUrl?: string;
  data: any[];
  render: (item: any) => React.ReactNode;
  itemWidth?: number;
  columns?: number;
  onViewAllClick?: () => void;
  loading?: boolean;
}

export default function AdvancedCarousel({
  title,
  imageUrl,
  data,
  render,
  itemWidth,
  columns,
  onViewAllClick = () => {},
  loading = false,
}: AdvancedCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const { t, i18n } = useTranslation();

  const isRTL = i18n.dir(i18n.language) === "rtl";

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
  }, [api]);

  useEffect(() => {
    setTimeout(() => {
      setCanScrollPrev(api?.canScrollPrev() || false);
      setCanScrollNext(api?.canScrollNext() || false);
    }, 100);
  }, [api, data]);

  const renderLoadingItems = () => {
    return Array.from({ length: 5 }).map((_, i) => (
      <CarouselItem
        key={i}
        className="basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5"
      >
        <Skeleton
          className="rounded-xl h-48 sm:h-56 md:h-64"
          height={256}
          duration={0.5}
        />
      </CarouselItem>
    ));
  };

  return (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="flex gap-2 sm:gap-4 items-center justify-between w-full flex-wrap">
        <div className="flex items-center gap-2 sm:gap-4">
          {imageUrl && (
            <img
              src={imageUrl}
              className="object-contain h-14 w-14 sm:h-20 sm:w-20 rounded-full"
            />
          )}
          <h1 className="text-lg sm:text-2xl font-semibold">{title}</h1>
        </div>

        <div className="flex gap-2 sm:gap-4 items-center">
          <span
            className="text-xs sm:text-sm text-primary cursor-pointer"
            onClick={onViewAllClick}
          >
            {t("see_all")}
          </span>
          <div className="flex gap-1 items-center">
            <button
              disabled={!canScrollPrev}
              onClick={() => {
                api?.scrollPrev();
              }}
              className="bg-primary-light/80 p-2 rounded-full cursor-pointer hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRTL ? (
                <ArrowRight size={18} color="#E78E22" />
              ) : (
                <ArrowLeft size={18} color="#E78E22" />
              )}
            </button>
            <button
              disabled={!canScrollNext}
              onClick={() => {
                api?.scrollNext();
              }}
              className="bg-primary-light/80 p-2 rounded-full cursor-pointer hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRTL ? (
                <ArrowLeft size={18} color="#E78E22" />
              ) : (
                <ArrowRight size={18} color="#E78E22" />
              )}
            </button>
          </div>
        </div>
      </div>
      <Carousel
        className="w-full"
        plugins={[WheelGesturesPlugin()]}
        opts={{ skipSnaps: true, direction: isRTL ? "rtl" : "ltr" }}
        setApi={setApi}
      >
        <CarouselContent className="items-center ps-0.5 sm:ps-1 md:ps-0">
          {loading
            ? renderLoadingItems()
            : data.map((item, index) => (
                <CarouselItem
                  key={index}
                  className={columns ? `basis-${columns}` : "flex-none"}
                  style={{ width: itemWidth }}
                >
                  {render(item)}
                </CarouselItem>
              ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
